import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PlusIcon, XMarkIcon, ChevronDownIcon, ChevronUpIcon } from "../../../automationStudioIcons";
import SmartInputBox from "../../shared/SmartInputBox";
import ButtonComponent from "../../../../customButton";
import {
    buildClearedInstructions,
    canAddInstructionBelow,
    getAddInstructionBelowTooltip,
} from '../../../../../features/automationStudioNew/planUtils';
import { selectDirtyInstructionIds } from '../../../../../features/automationStudioNew/automationStudioNewSelectors';
import { resetInstructionTextFromBaseline } from '../../../../../features/automationStudioNew/automationStudioNewSlice';
import styles from "../../../../../styles/automationStudio/automationStudio.module.scss";

/**
 * InstructionsView - Enhanced instructions management interface (presentational)
 */
export default function InstructionsView({
    instructions = [],
    onInstructionsChange,
    onClearInstructions,
    onViewResults,
    testCompleted = false,
    fileSuggestions = [],
    fileDescriptions = {},
    isExecuting = false,
    showDynamicScriptChip = false,
    variablesList = [],
    variablesListDescriptions = {},
}) {
    const dispatch = useDispatch();
    const dirtyInstructionIds = useSelector(selectDirtyInstructionIds);
    const visibleInstructions = instructions.filter((inst) => !inst.removedFromEditor);
    const visibleCount = visibleInstructions.length;
    const [tipsExpanded, setTipsExpanded] = useState(false);
    const listRef = useRef(null);
    const pendingFocusIndexRef = useRef(null);

    useEffect(() => {
        if (pendingFocusIndexRef.current === null) return;
        const idx = pendingFocusIndexRef.current;
        pendingFocusIndexRef.current = null;
        const el = listRef.current?.querySelector(
            `[data-instruction-index="${idx}"] textarea, [data-instruction-index="${idx}"] input`
        );
        el?.focus();
    }, [instructions]);

    const hasInstructions = visibleInstructions.some((inst) => inst.text.trim());
    const totalInstructions = visibleInstructions.filter((inst) => inst.text.trim()).length;
    const totalStepsInPlan = instructions.reduce(
        (sum, inst) => sum + (inst.aiSteps?.length || 0),
        0
    );
    const hasSteps = totalStepsInPlan > 0;

    const handleInstructionChange = (index, value) => {
        const updated = [...instructions];
        updated[index] = { ...updated[index], text: value };
        onInstructionsChange(updated);
    };

    const handleAddInstruction = (afterIndex) => {
        pendingFocusIndexRef.current = afterIndex + 1;
        const updated = [...instructions];
        updated.splice(afterIndex + 1, 0, { id: Date.now(), text: '', aiSteps: [] });
        onInstructionsChange(updated);
    };

    const handleRemoveInstruction = (index) => {
        if (visibleCount <= 1 || instructions[index]?.removedFromEditor) return;
        const updated = [...instructions];
        updated[index] = { ...instructions[index], removedFromEditor: true };
        onInstructionsChange(updated);
    };

    const handleMultiLinePaste = (index, { firstLine, extraLines }) => {
        if (isExecuting) return;
        const updated = [...instructions];
        const current = updated[index] ?? { id: Date.now(), text: '', aiSteps: [] };
        updated[index] = { ...current, text: firstLine };
        const extraInstructions = extraLines.map((line, offset) => ({
            id: Date.now() + offset + 1,
            text: line,
            aiSteps: [],
        }));
        if (extraInstructions.length) {
            updated.splice(index + 1, 0, ...extraInstructions);
        }
        onInstructionsChange(updated);
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (canAddInstructionBelow(instructions, index)) handleAddInstruction(index);
        }
    };

    const handleClearAll = () => {
        if (onClearInstructions) {
            onClearInstructions();
        } else {
            onInstructionsChange(buildClearedInstructions(instructions));
        }
    };

    return (
        <div className={styles.instructionsViewContainer}>
            {isExecuting && onViewResults ? (
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#eff6ff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                        <span className={`${styles.asTypoBody2}`} style={{ fontSize: '13px', fontWeight: 600, color: '#1d4ed8' }}>
                            Automation is running
                        </span>
                        {totalStepsInPlan > 0 && (
                            <span className={styles.asTypoCaption} style={{ fontSize: '11px', color: '#64748B' }}>
                                {totalStepsInPlan} step{totalStepsInPlan !== 1 ? 's' : ''} in plan
                            </span>
                        )}
                    </div>
                    <ButtonComponent
                        ctaText="View live execution"
                        variant="primary"
                        size="small"
                        className="white bg-primary"
                        onClick={onViewResults}
                    />
                </div>
            ) : testCompleted && onViewResults ? (
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span className={`${styles.asTypoBody2}`} style={{ fontSize: '13px', fontWeight: 600, color: '#16A34A' }}>
                            ✓ Execution Complete
                        </span>
                        <span className={styles.asTypoCaption} style={{ fontSize: '11px', color: '#64748B' }}>
                            {totalStepsInPlan} steps executed
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <ButtonComponent
                            ctaText="View Execution Results"
                            variant="primary"
                            size="small"
                            className="white bg-primary"
                            onClick={onViewResults}
                        />
                    </div>
                </div>
            ) : null}

            <div ref={listRef} className={styles.instructionsListArea}>
                {visibleCount === 0 ? (
                    <div className={styles.emptyInstructionsState}>
                        <p className={`${styles.asTypoBody2} ${styles.asTextSecondary}`}>
                            Add your first instruction to begin
                        </p>
                    </div>
                ) : (
                    (() => {
                        let displayNumber = 0;
                        return instructions.map((instruction, index) => {
                            if (instruction.removedFromEditor) return null;
                            displayNumber += 1;
                            const isInstructionDirty = dirtyInstructionIds.includes(String(instruction.id));
                            return (
                        <div key={instruction.id} className={styles.instructionRow}>
                            <div className={styles.instructionNumberBadge}>
                                {displayNumber}
                            </div>
                            <div
                                className={isInstructionDirty ? styles.dirtyFieldTarget : undefined}
                                style={{ flex: 1 }}
                                data-instruction-index={index}
                            >
                                <SmartInputBox
                                    size="small"
                                    value={instruction.text}
                                    onChange={(e) => handleInstructionChange(index, e.target.value)}
                                    onMultiLinePaste={(payload) => handleMultiLinePaste(index, payload)}
                                    onKeyDown={(e) => !isExecuting && handleKeyDown(e, index)}
                                    placeholder="Type ${ to add variables, @ to add files"
                                    customClassName={styles.instructionInput}
                                    variableTrigger="${"
                                    suggestions={variablesList}
                                    descriptions={variablesListDescriptions}
                                    fileTrigger="@"
                                    fileSuggestions={fileSuggestions}
                                    fileDescriptions={fileDescriptions}
                                    formatFileInsertion={(file) => `@[${file.id}]`}
                                    disabled={isExecuting}
                                />
                            </div>
                            <div className={styles.instructionActions}>
                                {(() => {
                                    const canAdd = canAddInstructionBelow(instructions, index);
                                    const addTooltip = getAddInstructionBelowTooltip(instructions, index);
                                    return (
                                        <span className={addTooltip ? styles.tooltipHost : styles.tooltipSpan} title={addTooltip || undefined}>
                                            <button
                                                type="button"
                                                onClick={() => handleAddInstruction(index)}
                                                className={`${styles.asIconButton} ${styles.instructionAddButton}`}
                                                disabled={!canAdd || isExecuting}
                                                aria-label="Add instruction below"
                                            >
                                                <PlusIcon className={styles.actionIcon} />
                                            </button>
                                        </span>
                                    );
                                })()}
                                <span className={styles.tooltipHost} title="Remove instruction">
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveInstruction(index)}
                                        className={`${styles.asIconButton} ${styles.instructionDeleteButton}`}
                                        disabled={visibleCount <= 1 || isExecuting}
                                        aria-label="Remove instruction"
                                    >
                                        <XMarkIcon className={styles.actionIcon} />
                                    </button>
                                </span>
                            </div>
                        </div>
                            );
                        });
                    })()
                )}
            </div>

            <div className={styles.generationSection}>
                {hasSteps && (
                    <div className={styles.quickStats}>
                        <div className={styles.quickStatsInner}>
                            <span className={`${styles.asTypoCaption} ${styles.asTextSecondary}`}>
                                {totalInstructions} instruction{totalInstructions !== 1 ? 's' : ''} → {totalStepsInPlan} step{totalStepsInPlan !== 1 ? 's' : ''} generated
                            </span>
                            {showDynamicScriptChip ? (
                                <span className={styles.quickStatsDynamicChip} role="status" aria-label="Loaded automation script">
                                    Dynamic
                                </span>
                            ) : null}
                        </div>
                    </div>
                )}

                <div className={styles.tipsSection}>
                    <div
                        className={styles.tipsHeader}
                        onClick={() => setTipsExpanded(!tipsExpanded)}
                    >
                        <span className={`${styles.tipsTitle} ${styles.asTypoCaption}`}>
                            💡 Tips for better results
                        </span>
                        <button type="button" className={`${styles.asIconButton}`} style={{ padding: '2px' }} aria-expanded={tipsExpanded} aria-label={tipsExpanded ? 'Collapse tips' : 'Expand tips'}>
                            {tipsExpanded ? (
                                <ChevronUpIcon className="icon" />
                            ) : (
                                <ChevronDownIcon className="icon" />
                            )}
                        </button>
                    </div>
                    <div className={`${styles.asCollapse} ${tipsExpanded ? styles.asCollapseOpen : ''}`}>
                        <div className={styles.tipsContent}>
                            <p className={`${styles.tipItem} ${styles.asTypoCaption}`}>
                                • Use clear, action-oriented language
                            </p>
                            <p className={`${styles.tipItem} ${styles.asTypoCaption}`}>
                                • Example: "Click the login button"
                            </p>
                            <p className={`${styles.tipItem} ${styles.asTypoCaption}`}>
                                • Be specific about elements and actions
                            </p>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                    <ButtonComponent
                        ctaText="Clear Instructions"
                        variant="secondary"
                        size="small"
                        className="slate900 bg-slate50"
                        onClick={handleClearAll}
                        disabled={!hasInstructions || isExecuting}
                    />
                    <ButtonComponent
                        ctaText="Reset Instructions"
                        variant="secondary"
                        size="small"
                        className="slate900 bg-slate50"
                        onClick={() => dispatch(resetInstructionTextFromBaseline())}
                        disabled={!dirtyInstructionIds.length || isExecuting}
                    />
                </div>
            </div>
        </div>
    );
}
